import React, { useState, useEffect } from 'react';
import './CommonStyles.css';
import './SendNotificationModal.css';
import apiService from '../../services/api';

const SendNotificationModal = ({ sale, onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('email');
  const [emailData, setEmailData] = useState({
    to: '',
    subject: 'Thank you from {business_name}',
    cc: '',
    bcc: '',
    body: ''
  });
  const [smsData, setSmsData] = useState({
    to: '',
    message: ''
  });
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (sale) {
      const apiData = sale.apiData || sale;
      const customerEmail = apiData.contact?.email || sale.customerEmail || '';
      const customerPhone = apiData.contact?.mobile || sale.contactNumber || '';
      
      setEmailData(prev => ({
        ...prev,
        to: customerEmail
      }));
      
      setSmsData(prev => ({
        ...prev,
        to: customerPhone
      }));
    }
  }, [sale]);

  const availableTags = [
    '{business_name}', '{business_logo}',
    '{invoice_number}', '{invoice_url}', '{total_amount}', '{paid_amount}', '{due_amount}', '{cumulative_due_amount}', '{due_date}',
    '{location_name}', '{location_address}', '{location_email}', '{location_phone}', '{location_custom_field_1}', '{location_custom_field_2}', '{location_custom_field_3}', '{location_custom_field_4}',
    '{contact_name}', '{contact_custom_field_1}', '{contact_custom_field_2}', '{contact_custom_field_3}', '{contact_custom_field_4}', '{contact_custom_field_5}', '{contact_custom_field_6}', '{contact_custom_field_7}', '{contact_custom_field_8}', '{contact_custom_field_9}', '{contact_custom_field_10}',
    '{sell_custom_field_1}', '{sell_custom_field_2}', '{sell_custom_field_3}', '{sell_custom_field_4}',
    '{shipping_custom_field_1}', '{shipping_custom_field_2}', '{shipping_custom_field_3}', '{shipping_custom_field_4}', '{shipping_custom_field_5}'
  ];

  const insertTag = (tag) => {
    if (activeTab === 'email') {
      setEmailData(prev => ({
        ...prev,
        body: prev.body + tag
      }));
    } else {
      setSmsData(prev => ({
        ...prev,
        message: prev.message + tag
      }));
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setIsSending(true);

    try {
      await apiService.sendSaleNotification({
        sale_id: sale.id,
        type: 'email',
        to: emailData.to,
        subject: emailData.subject,
        cc: emailData.cc,
        bcc: emailData.bcc,
        body: emailData.body
      });

      alert('Email sent successfully!');
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleSendSMS = async (e) => {
    e.preventDefault();
    setIsSending(true);

    try {
      await apiService.sendSaleNotification({
        sale_id: sale.id,
        type: 'sms',
        to: smsData.to,
        message: smsData.message
      });

      alert('SMS/WhatsApp notification sent successfully!');
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Error sending SMS:', error);
      alert('Failed to send SMS. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  if (!sale) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container notification-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Send Notification - New Sale</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-content notification-content">
          {/* Available Tags Section */}
          <div className="available-tags-section">
            <h3>Available Tags:</h3>
            <div className="tags-container">
              {availableTags.map((tag, index) => (
                <button
                  key={index}
                  type="button"
                  className="tag-btn"
                  onClick={() => insertTag(tag)}
                  title="Click to insert"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="notification-tabs">
            <button
              className={`tab-btn ${activeTab === 'email' ? 'active' : ''}`}
              onClick={() => setActiveTab('email')}
            >
              📧 Send Email
            </button>
            <button
              className={`tab-btn ${activeTab === 'sms' ? 'active' : ''}`}
              onClick={() => setActiveTab('sms')}
            >
              📱 Send SMS/WhatsApp
            </button>
          </div>

          {/* Email Form */}
          {activeTab === 'email' && (
            <form onSubmit={handleSendEmail} className="notification-form">
              <div className="form-group">
                <label>To:*</label>
                <input
                  type="email"
                  value={emailData.to}
                  onChange={(e) => setEmailData({...emailData, to: e.target.value})}
                  placeholder="To"
                  required
                />
              </div>

              <div className="form-group">
                <label>Email Subject:*</label>
                <input
                  type="text"
                  value={emailData.subject}
                  onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                  placeholder="Email Subject"
                  required
                />
              </div>

              <div className="form-group">
                <label>CC:</label>
                <input
                  type="email"
                  value={emailData.cc}
                  onChange={(e) => setEmailData({...emailData, cc: e.target.value})}
                  placeholder="CC"
                />
              </div>

              <div className="form-group">
                <label>BCC:</label>
                <input
                  type="email"
                  value={emailData.bcc}
                  onChange={(e) => setEmailData({...emailData, bcc: e.target.value})}
                  placeholder="BCC"
                />
              </div>

              <div className="form-group">
                <label>Email Body:*</label>
                <textarea
                  value={emailData.body}
                  onChange={(e) => setEmailData({...emailData, body: e.target.value})}
                  placeholder="Enter email body here... Use available tags above."
                  rows="10"
                  required
                />
                <div className="editor-info">
                  <small>{emailData.body.split(/\s+/).filter(w => w).length} words</small>
                  <small style={{ marginLeft: '1rem', color: 'var(--etpos-text-muted)' }}>
                    Tip: Click on tags above to insert them
                  </small>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isSending}>
                  {isSending ? 'Sending...' : '📧 Send Email'}
                </button>
              </div>
            </form>
          )}

          {/* SMS Form */}
          {activeTab === 'sms' && (
            <form onSubmit={handleSendSMS} className="notification-form">
              <div className="form-group">
                <label>To (Phone Number):*</label>
                <input
                  type="tel"
                  value={smsData.to}
                  onChange={(e) => setSmsData({...smsData, to: e.target.value})}
                  placeholder="+1234567890"
                  required
                />
              </div>

              <div className="form-group">
                <label>Message:*</label>
                <textarea
                  value={smsData.message}
                  onChange={(e) => setSmsData({...smsData, message: e.target.value})}
                  placeholder="Enter SMS/WhatsApp message here... Use available tags above."
                  rows="8"
                  required
                />
                <div className="editor-info">
                  <small>{smsData.message.length} characters</small>
                  <small style={{ marginLeft: '1rem', color: 'var(--etpos-text-muted)' }}>
                    Tip: Click on tags above to insert them
                  </small>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isSending}>
                  {isSending ? 'Sending...' : '📱 Send SMS/WhatsApp'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SendNotificationModal;
